const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const getToken = () => localStorage.getItem('saanvi_admin_token');

const headers = (extra = {}) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
  ...extra,
});

// ── Auth ──────────────────────────────────────────────────────
export const login = (username, password) =>
  fetch(`${BASE}/api/admin/login`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  }).then(r => r.json());

export const getMe = () =>
  fetch(`${BASE}/api/admin/me`, { headers: headers() }).then(r => r.json());

export const getStats = () =>
  fetch(`${BASE}/api/admin/stats`, { headers: headers() }).then(r => r.json());

// ── Services ──────────────────────────────────────────────────
export const getServicesAll = () =>
  fetch(`${BASE}/api/services/all`, { headers: headers() }).then(r => r.json());

export const createService = (data) =>
  fetch(`${BASE}/api/services`, {
    method: 'POST', headers: headers(), body: JSON.stringify(data),
  }).then(r => r.json());

export const updateService = (id, data) =>
  fetch(`${BASE}/api/services/${id}`, {
    method: 'PUT', headers: headers(), body: JSON.stringify(data),
  }).then(r => r.json());

export const deleteService = (id) =>
  fetch(`${BASE}/api/services/${id}`, {
    method: 'DELETE', headers: headers(),
  }).then(r => r.json());

// ── Portfolio ─────────────────────────────────────────────────
export const getPortfolioAll = () =>
  fetch(`${BASE}/api/portfolio/all`, { headers: headers() }).then(r => r.json());

// Upload with progress tracking via XMLHttpRequest
function uploadWithProgress(url, method, formData, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);

    // Upload progress
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.onload = () => {
      try {
        resolve(JSON.parse(xhr.responseText));
      } catch {
        resolve({ success: false, message: `Server error (${xhr.status})` });
      }
    };

    xhr.onerror = () => reject(new Error('Network error. Check your connection.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out. Try a smaller file or check your connection.'));
    xhr.timeout = 30 * 60 * 1000; // 30 minutes for multi-video uploads

    xhr.send(formData);
  });
}

export const createPortfolio = (formData, onProgress) =>
  uploadWithProgress(`${BASE}/api/portfolio`, 'POST', formData, onProgress);

export const updatePortfolio = (id, formData, onProgress) =>
  uploadWithProgress(`${BASE}/api/portfolio/${id}`, 'PUT', formData, onProgress);

export const deletePortfolio = (id) =>
  fetch(`${BASE}/api/portfolio/${id}`, {
    method: 'DELETE', headers: headers(),
  }).then(r => r.json());

export const togglePortfolioHome = (id) =>
  fetch(`${BASE}/api/portfolio/${id}/toggle-home`, {
    method: 'PUT', headers: headers(),
  }).then(r => r.json());

export const seedPortfolio = () =>
  fetch(`${BASE}/api/portfolio/seed`, {
    method: 'POST', headers: headers(),
  }).then(r => r.json());

// ── Promotions ────────────────────────────────────────────────
export const getPromotionsAll = () =>
  fetch(`${BASE}/api/promotions/all`, { headers: headers() }).then(r => r.json());

// Use XHR for promotions uploads so large video files work without timeout
function uploadPromoXHR(url, method, formData, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.withCredentials = true; // must match server's Access-Control-Allow-Credentials: true
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    if (onProgress) xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.onload = () => {
      if (xhr.status === 0) {
        reject(new Error('Network error. Check your connection.'));
        return;
      }
      try { resolve(JSON.parse(xhr.responseText)); }
      catch { resolve({ success: false, message: `Server error (${xhr.status})` }); }
    };
    xhr.onerror = () => reject(new Error('Network error. Check your connection.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out. Try a smaller file or check your connection.'));
    xhr.timeout = 10 * 60 * 1000; // 10 minutes for large videos
    xhr.send(formData);
  });
}

export const createPromotion = (formData, onProgress) =>
  uploadPromoXHR(`${BASE}/api/promotions`, 'POST', formData, onProgress);

export const updatePromotion = (id, formData, onProgress) =>
  uploadPromoXHR(`${BASE}/api/promotions/${id}`, 'PUT', formData, onProgress);

export const deletePromotion = (id) =>
  fetch(`${BASE}/api/promotions/${id}`, {
    method: 'DELETE', headers: headers(),
  }).then(r => r.json());

// ── Pricing ───────────────────────────────────────────────────
export const getPricingAll = () =>
  fetch(`${BASE}/api/pricing/all`, { headers: headers() }).then(r => r.json());

export const createPricing = (data) =>
  fetch(`${BASE}/api/pricing`, {
    method: 'POST', headers: headers(), body: JSON.stringify(data),
  }).then(r => r.json());

export const updatePricing = (id, data) =>
  fetch(`${BASE}/api/pricing/${id}`, {
    method: 'PUT', headers: headers(), body: JSON.stringify(data),
  }).then(r => r.json());

export const deletePricing = (id) =>
  fetch(`${BASE}/api/pricing/${id}`, {
    method: 'DELETE', headers: headers(),
  }).then(r => r.json());

// ── Messages ──────────────────────────────────────────────────
export const getMessages = () =>
  fetch(`${BASE}/api/admin/messages`, { headers: headers() }).then(r => r.json());

export const updateMessageStatus = (id, status) =>
  fetch(`${BASE}/api/admin/messages/${id}`, {
    method: 'PUT', headers: headers(), body: JSON.stringify({ status }),
  }).then(r => r.json());

export const deleteMessage = (id) =>
  fetch(`${BASE}/api/admin/messages/${id}`, {
    method: 'DELETE', headers: headers(),
  }).then(r => r.json());

// ── Quotes ────────────────────────────────────────────────────
export const getQuotes = () =>
  fetch(`${BASE}/api/admin/quotes`, { headers: headers() }).then(r => r.json());

export const updateQuoteStatus = (id, status) =>
  fetch(`${BASE}/api/admin/quotes/${id}`, {
    method: 'PUT', headers: headers(), body: JSON.stringify({ status }),
  }).then(r => r.json());

export const deleteQuote = (id) =>
  fetch(`${BASE}/api/admin/quotes/${id}`, {
    method: 'DELETE', headers: headers(),
  }).then(r => r.json());
